import React, { useState } from 'react';
import UiButton from '@components/ui/button/UiButton';
import { AppPositionElement, PositionStatus } from '@models/positions';
import LocationIcon from '@assets/svgs/location-icon.svg';
import GlobeIcon from '@assets/svgs/earth-icon.svg';
import DownAngleIcon from '@assets/svgs/down-angle-icon.svg';
import LeafIcon from '@assets/svgs/leaf-icon.svg';
import NewTabIcon from '@assets/svgs/grey-open-tab-icon.svg';
import ArrowLeftIcon from '@assets/svgs/arrow-left.svg';
import AddNewUser from '@assets/svgs/add-new-user-icon.svg';
import GreyPencilIcon from '@assets/svgs/grey-big-pencil-icon.svg';
import BuildingIcon from '@assets/svgs/grey-building-icon.svg';
import InactivePositionsIcon from '@assets/svgs/inactive-positions-icon.svg';
import ActivePositionsIcon from '@assets/svgs/active-positions-purple-icon.svg';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import useNotification from '@hooks/notification/useNotification';
import StateEnum from '@constants/state.enum';
import { updatePosition } from '@services/position/position-http.service';
import { tagFormat } from '@services/date/date-format';
import CandidateModal from '@components/app-components/app-header/nav-bar/candidate-modal/CandidateModal';
import CustomTag from './custom-tag/CustomTag';
import styles from './PositionHeader.module.scss';
import { Dropdown, Menu, Tooltip } from 'antd';

export type PositionHeaderProps = {
  position: AppPositionElement;
  status: string;
  positionsPageNumber: string;
  fetchApplicants?: () => void;
  setStatus: (value: PositionStatus) => void;
};

const PositionHeader: React.FC<PositionHeaderProps> = ({
  fetchApplicants,
  position,
  status,
  positionsPageNumber,
  setStatus,
}) => {
  const { t: translate, i18n } = useTranslation(['position']);
  const [openCandidateModal, setOpenCandidateModal] = useState<boolean>(false);
  const router = useRouter();

  const [generalError] = useNotification(
    StateEnum.ERROR,
    'Something went wrong',
    'Oops, something went wrong, please try again later!',
  );

  const updateStatus = async (open: boolean) => {
    try {
      await updatePosition(
        {
          status: open,
          posted: false,
        },
        position.id,
      );
    } catch (e: any) {
      generalError();
    }
    setStatus(open ? 'open' : 'closed');
  };

  const setTagIcon = () => {
    if (status === 'closed') {
      return <InactivePositionsIcon />;
    }
    if (status === 'open') {
      return <ActivePositionsIcon />;
    }
    return <GlobeIcon />;
  };

  const tags = [
    {
      id: 1,
      title: translate(status),
      type: status,
      icon: setTagIcon(),
    },
    { id: 2, title: position.location || '-', type: 'info', icon: <LocationIcon /> },
    { id: 3, title: position.department || '-', type: 'info', icon: <BuildingIcon /> },
    {
      id: 4,
      title: position.date_inserted ? tagFormat(new Date(position.date_inserted)) : '-',
      type: 'info',
      icon: <LeafIcon />,
    },
  ];

  const changeStatusMenu = (
    <Menu mode="vertical">
      <Menu.Item key="open" onClick={() => updateStatus(true)} className="main-body-text">
        {translate('open')}
      </Menu.Item>
      <Menu.Item key="closed" onClick={() => updateStatus(false)} className="main-body-text">
        {translate('closed')}
      </Menu.Item>
    </Menu>
  );

  return (
    <div className={styles.wrapHeader}>
      <div className={styles.wrapHeaderAndTags}>
        <div>
          <div className={styles.wrapLeftButton}>
            <UiButton
              className={styles.backLeftButton}
              type="ghost"
              size="small"
              onClick={() =>
                router.push(
                  status === 'closed'
                    ? '/app/positions?status=inactive'
                    : `/app/positions?pageNumber=${positionsPageNumber}`,
                )
              }
              data-testid="go-back-button"
            >
              <div className={styles.backToPosition}>
                <ArrowLeftIcon />
                <div className={styles.backToPositionText}>{translate('all_jobs')}</div>
              </div>
            </UiButton>
          </div>
          <div className={styles.headerTitleIcon}>
            <div className={`h4 ${styles.headerTitle}`}>{position.title}</div>
            <div
              onClick={() => router.push(`/app/positions/${position.id}`)}
              className={styles.titleActionIcon}
              data-testid="edit_position"
            >
              <Tooltip placement="top" title={translate('edit_position')}>
                <GreyPencilIcon />
              </Tooltip>
            </div>
            {status === 'published' && (
              <div
                onClick={() =>
                  window.open(
                    `/${i18n.language === 'en' ? 'en/' : ''}${position.company.slug}/positions/${position.id}`,
                    '_blank',
                    'noreferrer',
                  )
                }
                className={styles.titleActionIcon}
              >
                <Tooltip placement="top" title={translate('show_position')}>
                  <NewTabIcon />
                </Tooltip>
              </div>
            )}
          </div>
        </div>
        <div className={styles.wrapTags}>
          {tags.map((tag) => (
            <span key={tag.id} className={styles.tag}>
              <CustomTag
                title={tag.title}
                type={tag.type}
                icon={tag.icon}
                updateStatus={(value) => updateStatus(value)}
              />
              {tag.id === 1 && status === 'published' ? (
                <Dropdown dropdownRender={() => changeStatusMenu} trigger={['click']} placement="bottomRight" arrow>
                  <div className={styles.anglePublishedTag}>
                    <DownAngleIcon />
                  </div>
                </Dropdown>
              ) : (
                <></>
              )}
            </span>
          ))}
        </div>
      </div>
      <div className={styles.wrapRightButton}>
        <UiButton
          className={styles.backRightButton}
          type="primary"
          size="small"
          onClick={() => setOpenCandidateModal(true)}
          icon={<AddNewUser />}
        >
          <div className={styles.backToPositionText}>{translate('add_candidate')}</div>
        </UiButton>
      </div>
      <CandidateModal
        positionId={position.id}
        fetchApplicants={() => {
          if (fetchApplicants) fetchApplicants();
        }}
        openCandidateModal={openCandidateModal}
        setOpenCandidateModal={setOpenCandidateModal}
      />
    </div>
  );
};

export default PositionHeader;
